import { useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { Loader2, Check, ArrowRight } from "lucide-react";
import { EMAILJS_CONFIG } from "@/lib/constants";
import { useReveal } from "@/hooks/useReveal";

export default function LeadMagnet() {
    const { ref, visible } = useReveal();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({ name: "", whatsapp: "", email: "" });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.whatsapp.trim()) {
            toast.error("Por favor completa todos los campos");
            return;
        }
        setSubmitting(true);
        try {
            await emailjs.send(
                EMAILJS_CONFIG.serviceId,
                EMAILJS_CONFIG.templateId,
                {
                    form_type: "Lead Magnet — Curso gratis",
                    name: form.name,
                    from_name: form.name,
                    email: form.email,
                    reply_to: form.email,
                    whatsapp: form.whatsapp,
                    message: `Solicitud del curso gratis "La imagen vende y vende bien" — Nombre: ${form.name} | WhatsApp: ${form.whatsapp} | Email: ${form.email}`,
                    to_email: EMAILJS_CONFIG.toEmail,
                },
                { publicKey: EMAILJS_CONFIG.publicKey }
            );
            setSubmitted(true);
            toast.success("¡Listo! Pronto recibirás el acceso al curso en tu correo.");
            setForm({ name: "", whatsapp: "", email: "" });
        } catch (err) {
            console.error("EmailJS error:", err);
            toast.error("No pudimos enviar tu solicitud. Inténtalo de nuevo.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="curso"
            data-testid="lead-magnet-section"
            className="relative py-28 md:py-40 bg-[#F7F3EE] overflow-hidden"
        >
            <div className="blur-shape w-[460px] h-[460px] bg-[#D8B9A7]/45 -bottom-40 -right-32" />

            <div
                ref={ref}
                className={`relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 reveal ${visible ? "is-visible" : ""}`}
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    {/* Left content */}
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="section-number">Curso gratis</span>
                            <span className="h-px w-12 bg-[#8C7C72]" />
                        </div>

                        <h2
                            data-testid="lead-magnet-title"
                            className="font-display text-[42px] md:text-[60px] leading-[1.04] text-[#1E1E1E] tracking-[-0.02em]"
                        >
                            “La imagen{" "}
                            <span className="font-italic-display text-[#C6A76A]">vende</span>{" "}
                            y vende bien”
                        </h2>

                        <p className="mt-8 text-base text-[#1E1E1E]/75 font-light max-w-md leading-relaxed">
                            Aprenderás:
                        </p>

                        <ul
                            data-testid="lead-magnet-learning-list"
                            className="mt-5 space-y-4 max-w-md"
                        >
                            {[
                                "Cómo usar tu imagen a tu favor",
                                "Errores que te restan presencia",
                                "Ajustes simples con alto impacto",
                            ].map((point, i) => (
                                <li
                                    key={point}
                                    data-testid={`lead-magnet-learning-${i}`}
                                    className="flex items-start gap-4"
                                >
                                    <span className="mt-1 flex items-center justify-center w-5 h-5 rounded-full bg-[#1E1E1E] text-[#F7F3EE] shrink-0">
                                        <Check size={11} strokeWidth={2.5} />
                                    </span>
                                    <span className="text-[15px] text-[#1E1E1E] font-light">
                                        {point}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Right form */}
                    <div className="lg:col-span-6">
                        <div className="relative bg-[#1E1E1E] text-[#F7F3EE] p-8 md:p-12 lg:p-14">
                            <div className="absolute -top-3 -right-3 w-full h-full border border-[#C6A76A] -z-0 hidden md:block" />
                            <div className="relative">
                                {submitted ? (
                                    <div
                                        data-testid="lead-magnet-success"
                                        className="py-10 text-center"
                                    >
                                        <div className="mx-auto w-14 h-14 rounded-full bg-[#C6A76A] flex items-center justify-center mb-6">
                                            <Check size={24} className="text-[#1E1E1E]" strokeWidth={2.5} />
                                        </div>
                                        <h3 className="font-display text-3xl mb-3">
                                            ¡Gracias!
                                        </h3>
                                        <p className="text-[#F7F3EE]/75 font-light max-w-xs mx-auto">
                                            Pronto recibirás el acceso al curso “La imagen vende y vende bien” en tu correo.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => setSubmitted(false)}
                                            data-testid="lead-magnet-reset"
                                            className="mt-8 text-[11px] tracking-[0.24em] uppercase text-[#C6A76A] underline underline-offset-4"
                                        >
                                            Enviar otro
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} data-testid="lead-magnet-form">
                                        <div className="text-[11px] tracking-[0.32em] uppercase text-[#C6A76A] mb-2">
                                            Acceso gratuito
                                        </div>
                                        <h3 className="font-display text-3xl md:text-4xl leading-tight mb-8">
                                            Quiero mi <span className="font-italic-display text-[#C6A76A]">curso gratis</span>
                                        </h3>

                                        <div className="space-y-7">
                                            <div>
                                                <label htmlFor="lm-name" className="text-[10px] tracking-[0.28em] uppercase text-[#F7F3EE]/55">
                                                    Nombre
                                                </label>
                                                <input
                                                    id="lm-name"
                                                    data-testid="lead-magnet-input-name"
                                                    type="text"
                                                    name="name"
                                                    value={form.name}
                                                    onChange={handleChange}
                                                    placeholder="Tu nombre completo"
                                                    className="editorial-input-dark"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="lm-whatsapp" className="text-[10px] tracking-[0.28em] uppercase text-[#F7F3EE]/55">
                                                    WhatsApp
                                                </label>
                                                <input
                                                    id="lm-whatsapp"
                                                    data-testid="lead-magnet-input-whatsapp"
                                                    type="tel"
                                                    name="whatsapp"
                                                    value={form.whatsapp}
                                                    onChange={handleChange}
                                                    placeholder="+52 ..."
                                                    className="editorial-input-dark"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="lm-email" className="text-[10px] tracking-[0.28em] uppercase text-[#F7F3EE]/55">
                                                    Email
                                                </label>
                                                <input
                                                    id="lm-email"
                                                    data-testid="lead-magnet-input-email"
                                                    type="email"
                                                    name="email"
                                                    value={form.email}
                                                    onChange={handleChange}
                                                    placeholder="tu@email.com"
                                                    className="editorial-input-dark"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            data-testid="lead-magnet-submit"
                                            disabled={submitting}
                                            className="mt-10 w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#C6A76A] text-[#1E1E1E] font-medium text-[13px] tracking-[0.18em] uppercase rounded-full hover:bg-[#F7F3EE] transition-all duration-400 disabled:opacity-60"
                                        >
                                            {submitting ? (
                                                <>
                                                    <Loader2 size={16} className="animate-spin" /> Enviando...
                                                </>
                                            ) : (
                                                <>
                                                    👉 Quiero mi curso gratis
                                                    <ArrowRight size={16} />
                                                </>
                                            )}
                                        </button>
                                        <p className="mt-5 text-[10px] tracking-[0.18em] uppercase text-[#F7F3EE]/45 text-center">
                                            Tus datos están seguros — no spam.
                                        </p>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
